import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Input,
    Select,
    Empty,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    CustomerServiceOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useGetMyTicketsQuery, useGetTicketCategoriesQuery } from '../services/nestjs-base-api';
import { useAuth } from '../hooks/useAuth';
import { ResponsiveTable } from '../admin/components/ResponsiveTable';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ITicket } from '../services/nestjs-base-api/types';
import CreateTicketModal from '../components/Support/CreateTicketModal';
import CustomerTicketDetailDrawer from '../components/Support/CustomerTicketDetailDrawer';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useScopedNavigate } from '../components/MarketerView/useScopedNavigate';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const SupportPage: React.FC = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const { ticketHash } = useParams<{ ticketHash?: string }>();
    const navigate = useScopedNavigate();

    // Status badge component (moved inside to access intl)
    const TicketStatusBadge: React.FC<{ status: ITicket['status'] }> = ({ status }) => {
        const statusConfig = {
            open: { color: 'blue', text: intl.formatMessage({ id: 'support.status.open' }) },
            in_progress: { color: 'processing', text: intl.formatMessage({ id: 'support.status.inProgress' }) },
            waiting_on_customer: { color: 'warning', text: intl.formatMessage({ id: 'support.status.waitingOnYou' }) },
            resolved: { color: 'success', text: intl.formatMessage({ id: 'support.status.resolved' }) },
            closed: { color: 'default', text: intl.formatMessage({ id: 'support.status.closed' }) },
        };

        const config = statusConfig[status];
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // Priority badge component (moved inside to access intl)
    const TicketPriorityBadge: React.FC<{ priority: ITicket['priority'] }> = ({ priority }) => {
        const priorityConfig = {
            low: { color: 'default', text: intl.formatMessage({ id: 'support.priority.low' }) },
            normal: { color: 'blue', text: intl.formatMessage({ id: 'support.priority.normal' }) },
            high: { color: 'orange', text: intl.formatMessage({ id: 'support.priority.high' }) },
            urgent: { color: 'red', text: intl.formatMessage({ id: 'support.priority.urgent' }) },
        };

        const config = priorityConfig[priority];
        return <Tag color={config.color}>{config.text}</Tag>;
    };
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>();
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [selectedTicketHash, setSelectedTicketHash] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);

    // Fetch data
    const {
        data: ticketsData,
        isLoading,
        refetch,
    } = useGetMyTicketsQuery({
        jwtToken: user?.jwtToken || '',
        params: {
            search: search || undefined,
            status: statusFilter,
            sortBy: 'created',
            sortOrder: 'DESC',
        },
    });

    const { data: categories } = useGetTicketCategoriesQuery({ jwtToken: user?.jwtToken || '' });

    const tickets = ticketsData?.tickets || [];
    const total = ticketsData?.total || 0;

    // Calculate status counts
    const statusCounts = tickets.reduce((acc, ticket) => {
        acc[ticket.status] = (acc[ticket.status] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    // Handle URL parameter for direct ticket access
    useEffect(() => {
        if (ticketHash && ticketHash !== selectedTicketHash) {
            setSelectedTicketHash(ticketHash);
            setDrawerVisible(true);
        }
    }, [ticketHash, selectedTicketHash]);

    // Table columns
    const columns: ColumnsType<ITicket> = [
        {
            title: intl.formatMessage({ id: 'support.table.ticketId' }),
            dataIndex: 'hash',
            key: 'hash',
            width: 120,
            render: (hash: string) => (
                <Text code style={{ whiteSpace: 'nowrap' }}>
                    {hash.substring(0, 8)}...
                </Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'support.table.subject' }),
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true,
            render: (subject: string, record: ITicket) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{subject}</div>
                    {record.category && (
                        <Tag color={record.category.color || 'default'} style={{ marginTop: 4 }}>
                            {record.category.name}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: intl.formatMessage({ id: 'support.table.status' }),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status: ITicket['status']) => <TicketStatusBadge status={status} />,
        },
        {
            title: intl.formatMessage({ id: 'support.table.priority' }),
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (priority: ITicket['priority']) => <TicketPriorityBadge priority={priority} />,
        },
        {
            title: intl.formatMessage({ id: 'support.table.lastUpdated' }),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            render: (date: string) => (
                <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
                    <span>{dayjs(date).fromNow()}</span>
                </Tooltip>
            ),
        },
        {
            title: intl.formatMessage({ id: 'support.table.created' }),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date: string) => (
                <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
                    <span>{dayjs(date).fromNow()}</span>
                </Tooltip>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <Title level={2}>
                    <CustomerServiceOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: 'support.page.title' })}
                </Title>
                <Text type="secondary">
                    {intl.formatMessage({ id: 'support.page.description' })}
                </Text>
            </div>

            {/* Stats Cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">{intl.formatMessage({ id: 'support.stats.open' })}</Text>
                            <Title level={2} style={{ margin: '8px 0', color: '#F59E0B' }}>
                                {statusCounts.open || 0}
                            </Title>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">{intl.formatMessage({ id: 'support.stats.inProgress' })}</Text>
                            <Title level={2} style={{ margin: '8px 0', color: '#faad14' }}>
                                {statusCounts.in_progress || 0}
                            </Title>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">{intl.formatMessage({ id: 'support.stats.resolved' })}</Text>
                            <Title level={2} style={{ margin: '8px 0', color: '#52c41a' }}>
                                {statusCounts.resolved || 0}
                            </Title>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">{intl.formatMessage({ id: 'support.stats.total' })}</Text>
                            <Title level={2} style={{ margin: '8px 0' }}>
                                {total}
                            </Title>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Filters and Create Button */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} md={10}>
                        <Search
                            placeholder={intl.formatMessage({ id: 'support.search.placeholder' })}
                            allowClear
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Select
                            placeholder={intl.formatMessage({ id: 'support.filter.status' })}
                            allowClear
                            style={{ width: '100%' }}
                            value={statusFilter}
                            onChange={setStatusFilter}
                        >
                            <Option value="open">{intl.formatMessage({ id: 'support.status.open' })}</Option>
                            <Option value="in_progress">{intl.formatMessage({ id: 'support.status.inProgress' })}</Option>
                            <Option value="waiting_on_customer">{intl.formatMessage({ id: 'support.status.waitingOnYou' })}</Option>
                            <Option value="resolved">{intl.formatMessage({ id: 'support.status.resolved' })}</Option>
                            <Option value="closed">{intl.formatMessage({ id: 'support.status.closed' })}</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                                {intl.formatMessage({ id: 'support.button.refresh' })}
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalVisible(true)}
                            >
                                {intl.formatMessage({ id: 'support.button.createTicket' })}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Tickets Table */}
            <Card>
                {tickets.length === 0 && !isLoading ? (
                    <Empty
                        description={intl.formatMessage({ id: 'support.empty.title' })}
                        style={{ padding: '40px 0' }}
                    >
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalVisible(true)}
                        >
                            {intl.formatMessage({ id: 'support.empty.button' })}
                        </Button>
                    </Empty>
                ) : (
                    <ResponsiveTable
                        columns={columns}
                        dataSource={tickets}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            total,
                            pageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total) => intl.formatMessage({ id: 'support.pagination.total' }, { total }),
                        }}
                        onRow={(record) => ({
                            onClick: () => {
                                navigate(`/dashboard/support/${record.hash}`);
                            },
                            style: { cursor: 'pointer' },
                        })}
                    />
                )}
            </Card>

            {/* Create Ticket Modal */}
            <CreateTicketModal
                visible={createModalVisible}
                onClose={() => setCreateModalVisible(false)}
                onSuccess={(ticketHash) => {
                    setCreateModalVisible(false);
                    refetch();
                    // Navigate to the newly created ticket
                    navigate(`/dashboard/support/${ticketHash}`);
                }}
            />

            {/* Ticket Detail Drawer */}
            <CustomerTicketDetailDrawer
                ticketHash={selectedTicketHash}
                visible={drawerVisible}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedTicketHash(null);
                    navigate('/dashboard/support');
                    refetch();
                }}
            />
        </div>
    );
};

export default SupportPage;
